import type { CommunityAdminStanding } from "@squeek/module-contracts/community-admin-v1";
import { ParticipantMark } from "@squeek/ui";
import { useState } from "react";
import { compactNumber, shortAddress, shortId } from "./format";
import type { RiskAssessment } from "./risk";

interface PersonIdentity {
  personId: string;
  x?: { name: string; username: string } | null;
  ensName?: string | null;
  discord?: { username: string } | null;
}

/** The name a reviewer recognises; never a bare id-like string. */
export function personLabel(person: PersonIdentity): string {
  const name = person.x?.name.trim();
  const idLike = /^(?:0x)?[a-f0-9-]{8,}$/i;
  return (name && !idLike.test(name) ? name : null) || (person.x?.username ? `@${person.x.username}` : null)
    || person.ensName?.trim() || (person.discord?.username && !idLike.test(person.discord.username) ? person.discord.username : null)
    || `Person ${shortId(person.personId)}`;
}

/** X picture (twimg, no referrer); the participant mark when absent or broken. */
export function PersonAvatar({ personId, profileImageUrl, size = 36 }: { personId: string; profileImageUrl: string | null; size?: number }) {
  const [failedUrl, setFailedUrl] = useState<string | null>(null);
  const src = size > 48 ? profileImageUrl?.replace(/_normal(?=\.)/, "_200x200") : profileImageUrl;
  return <span className="c-avatar" style={{ width: size, height: size }}>
    {src && src !== failedUrl
      ? <img src={src} alt="" width={size} height={size} loading="lazy" referrerPolicy="no-referrer" onError={() => setFailedUrl(src)} />
      : <ParticipantMark identity={personId} aria-hidden="true" />}
  </span>;
}

const TIER_LABEL = { gold: "Gold", silver: "Silver", bronze: "Bronze", member: "Member", unranked: "Unranked" } as const;

/** Tier as a coloured dot and word: tier is context, not an alert. */
export function TierMark({ standing }: { standing: CommunityAdminStanding | null }) {
  const tier = standing?.tier ?? "unranked";
  return <span className="c-tier" data-tier={tier}>{TIER_LABEL[tier]}</span>;
}

/** "481 Aura · #298" (or "direct include"); nothing when unranked. */
export function StandingLine({ standing }: { standing: CommunityAdminStanding | null }) {
  if (standing === null) return null;
  return <span className="c-fg-2 c-num admin-nowrap">{compactNumber(standing.aura)} Aura · {standing.rank === null ? "direct" : `#${standing.rank.toLocaleString("en")}`}</span>;
}

const RISK_TONE = { Disqualified: "bad", High: "bad", Watch: "warn", "No signals": null } as const;

/**
 * The one filled badge a row may carry. "No signals" is plain muted text; `reasons` shows the
 * lead reason (+n) beside the badge. The full list and the heuristic caveat are in the title.
 */
export function RiskBadge({ risk, reasons = false }: { risk: RiskAssessment; reasons?: boolean }) {
  const tone = RISK_TONE[risk.level];
  const title = `Heuristic, not a verdict${risk.reasons.length ? `: ${risk.reasons.join("; ")}` : ""}`;
  if (tone === null) return <span className="c-risk c-risk__none" title={title}>No signals</span>;
  const [lead, ...rest] = risk.reasons.filter((reason) => reason !== "Disqualified by moderation");
  return <span className="c-risk" title={title}>
    <span className="c-badge" data-tone={tone}>{risk.level}</span>
    {reasons && lead ? <span className="c-fg-2 c-truncate">{lead}{rest.length ? ` +${rest.length}` : ""}</span> : null}
  </span>;
}

/** Short display, full value on hover, one-click copy. */
export function CopyValue({ value, label }: { value: string; label?: string }) {
  const [result, setResult] = useState<string | null>(null);
  return <span className="c-copy">
    <span title={value} className="c-mono c-truncate">{value.length > 18 ? shortAddress(value) : value}</span>
    <button type="button" aria-label={`Copy ${label ?? value}`} onClick={() => { navigator.clipboard.writeText(value).then(() => setResult("Copied"), () => setResult("Failed")); }}>{result ?? "Copy"}</button>
  </span>;
}
